import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { HandledApiError } from "api/queryClient"
import {
  BaseCheckCleanStaleError,
  runBaseCheckClean,
} from "api/Repos/data-checks"
import type {
  BaseCheckCleanRequest,
  BaseCheckCleanResult,
  BaseCheckCleanSummary,
} from "types/dataChecks.types"
import { notify } from "shared/ui/Notifications/notify"

export interface CleanOffPathVariables {
  projectId: string
  owner: string
  request: BaseCheckCleanRequest
}

/**
 * What a clean did, in one sentence, for the toast.
 *
 * `respondentsAffected` is named only when it is present. `cellsBlanked` counts CELLS and the
 * word "cells" is always attached to it: the report's off-path figure counts respondents per
 * question, and the two numbers standing side by side without their units would look like a
 * discrepancy.
 */
export const describeClean = (clean: BaseCheckCleanSummary): string => {
  const plural = (n: number): string => (n === 1 ? "" : "s")
  const cells = `${clean.cellsBlanked.toLocaleString()} cell${plural(clean.cellsBlanked)}`
  const parts = [
    `${cells} blanked across ${clean.variablesBlanked.toLocaleString()} variable${plural(clean.variablesBlanked)} on ${clean.questionsCleaned.toLocaleString()} question${plural(clean.questionsCleaned)}`,
  ]
  if (clean.respondentsAffected !== undefined) {
    parts.push(
      `${clean.respondentsAffected.toLocaleString()} respondent${plural(clean.respondentsAffected)} affected`
    )
  }
  parts.push(`the data as it was is stored as "${clean.precleanDocument}"`)
  return `${parts.join(" · ")}.`
}

/**
 * The apply: blanks the answers the routing says were never asked.
 *
 * THE ONE MUTATION IN THIS FEATURE, and the shape of it is deliberate on three points.
 *
 * 1. `retry: false`, stated rather than inherited. A clean is a multi-second read-modify-write
 *    that ends in two repos writes; a silent second attempt after a failure the user never saw
 *    would be a second decision nobody made. (React Query does not retry mutations by default,
 *    but a default is not a promise — this one is load-bearing enough to write down.)
 * 2. A 200 is never turned into a rejection. Every outcome ABOUT THE PROJECT arrives at 200
 *    carrying `status` and `changed`, including the ones that failed part-way, and the panel
 *    renders all of them from `data`. Rejecting a `script_failed` would throw away the
 *    diagnostics and the log that say what went wrong. Only the endpoint's two refusals (409,
 *    400) and transport failures reject.
 * 3. Invalidation follows the WRITE, not the request. `changed: false` wrote nothing — no
 *    document, no data file, no audit record — so it invalidates nothing. A clean that DID
 *    write invalidates two roots:
 *      - `dataChecks.project(id)` — the cached report describes the data before the clean and
 *        is now historical.
 *      - `provenance.project(id)` — the clean appended `data.cleaned` to the tamper-evident
 *        chain and stored a document, so the Timeline, the verify badge and the stamp all move
 *        (as for anything that appends to the chain). Invalidated even when the history entry
 *        failed (`auditStatus: "not_recorded"`): the document and the data stamp still moved.
 *    Neither invalidation RE-RUNS the check: the base-check query is `enabled: false`, so it is
 *    marked stale and left alone. The panel asks for the new baseline explicitly, because a
 *    multi-second engine job should start when a person decides it should.
 *
 * Notifications live here, with the mutation — with one addition: a stale answer is NOT an error. It is the data having moved
 * under a report, so it is announced as information and the panel re-runs the check.
 *
 * @returns The react-query mutation. Call sites `await mutateAsync(...)` and follow up (refresh
 *   the report, show the summary); a rejection means the user has already been told.
 *
 * @example
 * const clean = useCleanOffPath()
 * const result = await clean.mutateAsync({
 *   projectId, owner,
 *   request: { scope: "all", variant: report.variant, confirmDataHash: report.dataHash,
 *     confirmQnrHash: report.qnrHash },
 * })
 */
export const useCleanOffPath = (): UseMutationResult<
  BaseCheckCleanResult,
  Error,
  CleanOffPathVariables
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: async (
      variables: CleanOffPathVariables
    ): Promise<BaseCheckCleanResult> =>
      runBaseCheckClean(
        await getAxios(),
        variables.projectId,
        variables.owner,
        variables.request
      ),
    onSuccess: (data, variables) => {
      if (!data.changed) {
        // Nothing was written. A failure status still deserves a toast — the panel keeps the
        // report and the reason on screen, but the user pressed a button and must be told it
        // did not do what they asked. An `ok` no-op is not a failure and is explained in the
        // panel by the server's own diagnostics, so it raises no toast.
        if (data.status !== "ok") {
          notify.error(
            "Nothing was cleaned",
            data.error || "The clean could not be completed."
          )
        }
        return
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.dataChecks.project(variables.projectId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.provenance.project(variables.projectId),
      })

      notify.success(
        "Off-path answers cleaned",
        data.clean
          ? describeClean(data.clean)
          : "The project's data was cleaned."
      )
      // Saved, not recorded: the panel says so where it will not vanish; the toast makes sure a
      // user who has already looked away hears it too.
      if (data.auditStatus === "not_recorded") {
        notify.toast.warning(
          "The data was cleaned, but its history entry could not be saved.",
          8
        )
      }
    },
    onError: (error: Error) => {
      if (error instanceof BaseCheckCleanStaleError) {
        // Deliberately not an error notification. Nothing went wrong and nothing was written —
        // the data simply moved since the report was run, so the approval no longer describes
        // it. The panel re-runs the check and says the same thing where it will not vanish.
        notify.toast.info(
          "The data or the questionnaire changed since this report, so the check has been re-run.",
          6
        )
        return
      }
      if (error instanceof HandledApiError) return
      notify.error("The clean could not be applied", error.message)
    },
  })
}

export default useCleanOffPath
